import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Update prices immediately before the server response
    this.#updatePricesForLine(line, quantity);

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Updates prices for a specific line item based on quantity.
   * @param {number} line - The line item index.
   * @param {number} quantity - The new quantity.
   */
  #updatePricesForLine(line, quantity) {
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const priceContainer = lineItemRow.querySelector('.product-price-container');
    if (!priceContainer) return;

    // Get the format from an existing price element to preserve formatting
    const getPriceFormat = (existingPriceEl) => {
      if (!existingPriceEl) return null;
      const currentText = existingPriceEl.textContent.trim();
      // Extract currency symbol and format pattern
      const match = currentText.match(/^([^0-9]*)([\d,.]+)([^0-9]*)$/);
      if (match) {
        return {
          prefix: match[1] || '',
          suffix: match[3] || '',
          hasDecimals: currentText.includes('.') && !currentText.match(/\.00$/),
        };
      }
      return null;
    };

    // Format money helper function that preserves format
    const formatPrice = (cents, format = null) => {
      const dollars = cents / 100;
      let formatted;
      
      if (format && !format.hasDecimals) {
        // If original format doesn't have decimals, remove .00
        formatted = dollars.toFixed(0);
      } else {
        formatted = dollars.toFixed(2);
        // Remove .00 if it's a whole number
        if (formatted.endsWith('.00')) {
          formatted = formatted.slice(0, -3);
        }
      }

      const prefix = format?.prefix || '';
      const suffix = format?.suffix || '';
      return `${prefix}${formatted}${suffix}`;
    };

    // Update compare-at price if it exists
    const compareAtPriceEl = priceContainer.querySelector('.compare-at-price');
    if (compareAtPriceEl) {
      const compareAtPrice = compareAtPriceEl.dataset.cartItemComparePrice || compareAtPriceEl.dataset.cartItemOriginalPrice;
      if (compareAtPrice) {
        const totalCompareAtPrice = parseInt(compareAtPrice, 10) * quantity;
        const format = getPriceFormat(compareAtPriceEl);
        compareAtPriceEl.textContent = formatPrice(totalCompareAtPrice, format);
      }
    }

    // Update discounted price or regular price
    const discountedPriceEl = priceContainer.querySelector('.discounted-price');
    const regularPriceEl = priceContainer.querySelector('.price');
    const priceEl = discountedPriceEl || regularPriceEl;

    if (priceEl) {
      const finalPrice = priceEl.dataset.cartItemFinalPrice || priceEl.dataset.cartItemOriginalPrice;
      if (finalPrice) {
        const totalPrice = parseInt(finalPrice, 10) * quantity;
        const format = getPriceFormat(priceEl);
        priceEl.textContent = formatPrice(totalPrice, format);
      }
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {

    console.log("updateQuantity::",config);

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        console.log("parsedResponseText::",parsedResponseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // If cart becomes empty, clear all discount codes
        if (newCartItemCount === 0) {
          this.#clearDiscountCodes();
        }

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error("cart item qty error::",error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    // cartItemError.textContent = parsedResponseText.errors;
    if (typeof window !== 'undefined' && typeof window['cart_item_error_text'] === 'string') {
      cartItemError.textContent = window['cart_item_error_text'];
    } else {
      cartItemError.textContent = parsedResponseText.errors;
    }
     
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Clears all discount codes when cart becomes empty.
   */
  #clearDiscountCodes() {
    const cartDiscountComponent = document.querySelector('cart-discount-component');
    if (!cartDiscountComponent) return;

    const existingDiscounts = cartDiscountComponent.querySelectorAll('.cart-discount__pill');
    if (existingDiscounts.length === 0) return;

    // Get all discount codes
    const discountCodes = Array.from(existingDiscounts)
      .map(pill => pill.dataset.discountCode)
      .filter(Boolean);

    if (discountCodes.length === 0) return;

    // Clear all discount codes by sending empty discount parameter
    const body = JSON.stringify({
      discount: '',
      sections: [this.sectionId],
    });

    fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
      .then(response => response.json())
      .then(data => {
        // Dispatch discount update event to refresh the UI
        document.dispatchEvent(new DiscountUpdateEvent(data, cartDiscountComponent.id));
        morphSection(this.sectionId, data.sections[this.sectionId]);
      })
      .catch(error => {
        console.error('Error clearing discount codes:', error);
      });
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
